import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Check, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MealPlansPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    goal: 'weight_loss',
    health_conditions: '',
    dietary_preferences: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/meal-plans/request`, formData);
      setSubmitted(true);
      toast.success('Meal plan request submitted!');
    } catch (error) {
      toast.error('Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const packages = [
    {
      name: 'Weekly Plan',
      price: 49.99,
      features: ['7-day meal plan', 'Shopping list', 'Recipes included', 'Email support'],
      popular: false
    },
    {
      name: 'Monthly Plan',
      price: 149.99,
      features: ['30-day meal plan', 'Weekly adjustments', '1 consultation call', 'Priority support', 'Recipe variations'],
      popular: true
    },
    {
      name: 'Premium Quarterly',
      price: 399.99,
      features: ['90-day meal plan', 'Bi-weekly adjustments', '4 consultation calls', '24/7 support', 'Custom recipes', 'Progress tracking'],
      popular: false
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center px-6"
        >
          <div className="w-20 h-20 bg-[#E5D3B3]/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-[#E5D3B3]" />
          </div>
          <h1 className="font-serif text-4xl text-[#F5F2EB] mb-4">Request Submitted!</h1>
          <p className="text-[#A3988F] mb-8 max-w-md mx-auto">
            Our nutrition experts will review your request and contact you within 24-48 hours with your personalized meal plan.
          </p>
          <Link to="/nutrition" className="btn-primary inline-flex items-center space-x-2" data-testid="meal-plans-back-btn">
            <span>Back to Nutrition</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      {/* Hero */}
      <section className="section-padding pb-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">Meal Plans</p>
            <h1 className="font-serif text-5xl md:text-6xl text-[#F5F2EB] mb-6">Custom Nutrition Plans</h1>
            <p className="text-lg text-[#A3988F]">
              Personalized meal plans designed by certified nutritionists to help you achieve your health goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages */}
      <section className="section-padding pt-0">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`luxury-card rounded-xl p-8 relative ${pkg.popular ? 'border-[#E5D3B3]/40' : ''}`}
              >
                {pkg.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#E5D3B3] text-[#0A0A0A] text-xs tracking-wider uppercase px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <h3 className="font-serif text-2xl text-[#F5F2EB] mb-2">{pkg.name}</h3>
                <div className="flex items-baseline mb-6">
                  <span className="text-4xl font-semibold text-[#E5D3B3]">${pkg.price}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center space-x-3 text-[#A3988F]">
                      <Check className="w-4 h-4 text-[#E5D3B3]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#request-form"
                  className={`w-full flex items-center justify-center space-x-2 ${pkg.popular ? 'btn-primary' : 'btn-secondary'}`}
                  data-testid={`meal-plan-${index}-btn`}
                >
                  <span>Get Started</span>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section id="request-form" className="section-padding bg-[#1A1514]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">Request Form</p>
            <h2 className="font-serif text-4xl text-[#F5F2EB]">Request Your Custom Meal Plan</h2>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="luxury-input"
                  required
                  data-testid="meal-plan-name-input"
                />
              </div>
              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="luxury-input"
                  required
                  data-testid="meal-plan-email-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="luxury-input"
                  required
                  data-testid="meal-plan-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm text-[#A3988F] mb-2">Goal</label>
                <select
                  value={formData.goal}
                  onChange={(e) => setFormData({...formData, goal: e.target.value})}
                  className="luxury-select"
                  data-testid="meal-plan-goal-select"
                >
                  <option value="weight_loss">Weight Loss</option>
                  <option value="weight_gain">Weight Gain</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="health_condition">Health Condition Management</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#A3988F] mb-2">Health Conditions (if any)</label>
              <input
                type="text"
                value={formData.health_conditions}
                onChange={(e) => setFormData({...formData, health_conditions: e.target.value})}
                className="luxury-input"
                placeholder="Diabetes, hypertension, allergies, etc."
                data-testid="meal-plan-conditions-input"
              />
            </div>

            <div>
              <label className="block text-sm text-[#A3988F] mb-2">Dietary Preferences</label>
              <input
                type="text"
                value={formData.dietary_preferences}
                onChange={(e) => setFormData({...formData, dietary_preferences: e.target.value})}
                className="luxury-input"
                placeholder="Vegetarian, vegan, gluten-free, etc."
                data-testid="meal-plan-preferences-input"
              />
            </div>

            <div>
              <label className="block text-sm text-[#A3988F] mb-2">Additional Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="luxury-input h-32 resize-none"
                placeholder="Any other information we should know..."
                data-testid="meal-plan-notes-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
              data-testid="meal-plan-submit-btn"
            >
              {loading ? (
                <div className="loader w-5 h-5" />
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  <span>Submit Request</span>
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default MealPlansPage;
